import { ApolloClient, NormalizedCacheObject } from "@apollo/client";
import { AgGrid, toast } from "@jsq/ds/core";
import {
  GetControlTowerWorkflowsDocument,
  GetJiraChildWorkItemsDocument,
  GetWorkflowDocument,
} from "#graphql/supergraphHooks";
import { WORKFLOW_EXPORT_PAGE_SIZE, WORKFLOW_TABLE_PAGE_SIZE } from "../../constants";
import { getWorkflowFilterVars } from "../utils/getWorkflowFilterVars";
import { flattenWorkflowsParentOnly } from "./workflowDataSource";
import { effectiveStatus, mapWorkflow } from "./workflowMappers";
import type {
  GetControlTowerWorkflowsQuery,
  GetControlTowerWorkflowsQueryVariables,
  GetJiraChildWorkItemsQuery,
  GetJiraChildWorkItemsQueryVariables,
  GetWorkflowQuery,
  GetWorkflowQueryVariables,
} from "#graphql/supergraphTypes";
import type { WorkflowFilterMode } from "../index";
import type { WorkflowInstanceRow, WorkflowTableRow } from "../types";

// AG Grid colId → BE sortable column. Only these columns are sortable: true in
// columnDefs; anything else falls through to the BE default (due_date desc).
const SORT_COLUMN_MAP: Record<string, string> = {
  dueDate: "due_date",
  createdAt: "created_at",
};

function buildSortVars(sortModel: AgGrid.IServerSideGetRowsRequest["sortModel"]): {
  sortBy?: string;
  sortDir?: string;
} {
  const first = sortModel?.[0];
  const sortBy = first ? SORT_COLUMN_MAP[first.colId] : undefined;
  if (!first || !sortBy) return {};
  return { sortBy, sortDir: first.sort.toUpperCase() };
}

// ---------------------------------------------------------------------------
// Datasource
// ---------------------------------------------------------------------------

export interface WorkflowsGraphqlDataSourceProps {
  apolloClient: ApolloClient<NormalizedCacheObject>;
  showExpanded: boolean;
  filterMode: WorkflowFilterMode;
  onViewerWorkflowUserIdResolved?: (id: string) => void;
}

export class WorkflowsGraphqlDataSource implements AgGrid.IServerSideDatasource {
  private client: ApolloClient<NormalizedCacheObject>;

  /** Full domain objects for the drawer click handler */
  private instanceCache: Map<string, WorkflowInstanceRow> = new Map();

  private showExpanded: boolean;
  private filterMode: WorkflowFilterMode;
  private onViewerWorkflowUserIdResolved?: (id: string) => void;
  private viewerWorkflowUserIdFired = false;

  /** Filters/sort from the most recent grid fetch — reused by fetchAllRootRows
   *  so "export all" reflects whatever the grid is currently showing. */
  private lastFilterModel: AgGrid.FilterModel | null = null;
  private lastSortModel: AgGrid.IServerSideGetRowsRequest["sortModel"] = [];

  constructor({
    apolloClient,
    showExpanded,
    filterMode,
    onViewerWorkflowUserIdResolved,
  }: WorkflowsGraphqlDataSourceProps) {
    this.client = apolloClient;
    this.showExpanded = showExpanded;
    this.filterMode = filterMode;
    this.onViewerWorkflowUserIdResolved = onViewerWorkflowUserIdResolved;
  }

  // ---------------------------------------------------------------------------
  // Setters — call then gridApi.refreshServerSide({ purge: true })
  // ---------------------------------------------------------------------------

  setShowExpanded(showExpanded: boolean): void {
    this.showExpanded = showExpanded;
    this.clearCaches();
  }

  setExternalFilters(filterMode: WorkflowFilterMode): void {
    this.filterMode = filterMode;
    this.clearCaches();
  }

  setOnViewerWorkflowUserIdResolved(callback: ((id: string) => void) | undefined): void {
    this.onViewerWorkflowUserIdResolved = callback;
  }

  private clearCaches(): void {
    this.instanceCache.clear();
  }

  getWorkflowInstance(workflowId: string): WorkflowInstanceRow | undefined {
    return this.instanceCache.get(workflowId);
  }

  // ---------------------------------------------------------------------------
  // IServerSideDatasource
  // ---------------------------------------------------------------------------

  getRows(params: AgGrid.IServerSideGetRowsParams): void {
    const { groupKeys } = params.request;

    if (groupKeys.length > 0) {
      this.fetchChildRows(params, groupKeys[0]);
    } else {
      this.fetchRootRows(params);
    }
  }

  // ---------------------------------------------------------------------------
  // Root-level — paginated workflows
  // ---------------------------------------------------------------------------

  private fetchRootRows(params: AgGrid.IServerSideGetRowsParams): void {
    const { startRow, endRow, filterModel, sortModel } = params.request;

    const resolvedStart = startRow ?? 0;
    const resolvedEnd = endRow ?? resolvedStart + WORKFLOW_TABLE_PAGE_SIZE;
    const limit = resolvedEnd - resolvedStart;
    const page = Math.floor(resolvedStart / limit) + 1;

    this.lastFilterModel = filterModel;
    this.lastSortModel = sortModel;

    const agGridFilters = getWorkflowFilterVars(filterModel);
    // "Assigned to me" — let the BE resolve the logged-in user from auth
    // (assignedToCurrentUser) rather than threading a client-supplied id; this
    // works without wiring currentUserId through and can't be spoofed.
    const externalFilters =
      this.filterMode === "assigned_to_me" ? { assignedToCurrentUser: true } : {};

    const variables: GetControlTowerWorkflowsQueryVariables = {
      ...agGridFilters,
      ...externalFilters,
      ...buildSortVars(sortModel),
      limit,
      page,
    };

    this.client
      .query<GetControlTowerWorkflowsQuery>({
        query: GetControlTowerWorkflowsDocument,
        variables,
        fetchPolicy: "no-cache",
      })
      .then(({ data }) => {
        const { items, pageInfo, viewerWorkflowUserId } = data.controlTowerWorkflows;
        if (viewerWorkflowUserId != null && !this.viewerWorkflowUserIdFired) {
          this.viewerWorkflowUserIdFired = true;
          this.onViewerWorkflowUserIdResolved?.(String(viewerWorkflowUserId));
        }
        const workflows = items.map(mapWorkflow);

        for (const wf of workflows) {
          // Cache domain object for the drawer click handler and the per-workflow
          // child-row refetch (needs sourceId). Child task rows are fetched fresh
          // on expand via fetchWorkflowEngineChildRows — not pre-cached here.
          this.instanceCache.set(String(wf.id), wf);
        }

        const parentRows: WorkflowTableRow[] = flattenWorkflowsParentOnly(workflows).map(
          (row) => ({ ...row, showIdSubtitle: this.showExpanded }),
        );

        params.success({ rowData: parentRows, rowCount: pageInfo.totalCount });

        if (parentRows.length === 0) {
          params.api?.showNoRowsOverlay();
        } else {
          params.api?.hideOverlay();
        }
      })
      .catch(() => {
        toast.error("Failed to load workflows");
        params.fail();
      });
  }

  // ---------------------------------------------------------------------------
  // Export — every root row matching the current filters/sort, not just the
  // page currently loaded in the grid. Pages through the BE's MAX_LIMIT cap.
  // ---------------------------------------------------------------------------

  async fetchAllRootRows(): Promise<WorkflowTableRow[]> {
    const agGridFilters = getWorkflowFilterVars(this.lastFilterModel);
    const externalFilters =
      this.filterMode === "assigned_to_me" ? { assignedToCurrentUser: true } : {};
    const sortVars = buildSortVars(this.lastSortModel);

    const rows: WorkflowTableRow[] = [];
    let page = 1;
    let totalCount = Infinity;

    while (rows.length < totalCount) {
      const { data } = await this.client.query<GetControlTowerWorkflowsQuery>({
        query: GetControlTowerWorkflowsDocument,
        variables: {
          ...agGridFilters,
          ...externalFilters,
          ...sortVars,
          limit: WORKFLOW_EXPORT_PAGE_SIZE,
          page,
        },
        fetchPolicy: "no-cache",
      });

      const { items, pageInfo } = data.controlTowerWorkflows;
      totalCount = pageInfo.totalCount;
      if (items.length === 0) break;

      const workflows = items.map(mapWorkflow);
      rows.push(
        ...flattenWorkflowsParentOnly(workflows).map((row) => ({
          ...row,
          showIdSubtitle: this.showExpanded,
        })),
      );
      page++;
    }

    return rows;
  }

  // ---------------------------------------------------------------------------
  // Child-level — tasks for a specific workflow
  // ---------------------------------------------------------------------------

  private fetchChildRows(params: AgGrid.IServerSideGetRowsParams, workflowId: string): void {
    const parentData = params.parentNode?.data as WorkflowTableRow | undefined;

    if (parentData?.source === "JIRA" && parentData.externalRefId) {
      this.fetchJiraChildRows(params, workflowId, parentData.externalRefId);
    } else {
      this.fetchWorkflowEngineChildRows(params, workflowId);
    }
  }

  /** Workflow-engine workflows: fetch the workflow's tasks fresh on every expand
   *  (combined with purgeClosedRowNodes, each collapse→expand re-pulls live
   *  task-step statuses). Keyed on the engine workflow id (sourceId). */
  private fetchWorkflowEngineChildRows(
    params: AgGrid.IServerSideGetRowsParams,
    workflowId: string,
  ): void {
    const sourceId = this.instanceCache.get(workflowId)?.sourceId;
    if (sourceId == null) {
      params.success({ rowData: [], rowCount: 0 });
      return;
    }

    this.client
      .query<GetWorkflowQuery, GetWorkflowQueryVariables>({
        query: GetWorkflowDocument,
        variables: { id: sourceId },
        fetchPolicy: "network-only",
      })
      .then(({ data }) => {
        const wf = data?.workflow;
        const currentStepName = wf?.currentStepName ?? null;
        const childRows: WorkflowTableRow[] = (wf?.tasks ?? []).map((task) => {
          const rawTaskId = String(task.id);
          return {
            id: `${workflowId}-task-${rawTaskId}`,
            path: [workflowId, `${workflowId}-task-${rawTaskId}`],
            isParentRow: false,
            rawTaskId,
            displayName: task.name,
            assignee: task.assignee
              ? { id: task.assignee.id, displayName: task.assignee.displayName }
              : null,
            dueDate: task.dueDate ?? null,
            status: effectiveStatus(task.status, task.name, currentStepName),
            gpxStatus: task.gpxStatus,
            stepOrder: task.stepOrder ?? undefined,
            taskType: task.taskType ?? undefined,
          };
        });

        // Refresh the parent row's volatile fields (status, progress, current
        // step, assignee) from the same response — no extra query. Keeps the
        // doughnut + status in sync with the freshly-expanded task list.
        const parentNode = params.parentNode;
        const parentData = parentNode?.data as WorkflowTableRow | undefined;
        if (wf && parentNode && parentData) {
          const inProgress = (wf.tasks ?? []).find((t) => t.status === "IN_PROGRESS");
          const stepName = currentStepName ?? inProgress?.name ?? undefined;
          const stepTask = (wf.tasks ?? []).find((t) => t.name === stepName) ?? inProgress;
          parentNode.setData({
            ...parentData,
            status: wf.status,
            statusCategory: wf.statusCategory ?? null,
            progress: wf.progress
              ? {
                  completed: wf.progress.completed,
                  total: wf.progress.total,
                  percentage: wf.progress.percentage,
                }
              : parentData.progress,
            currentStepName: stepName,
            currentStepTaskId: stepTask ? String(stepTask.id) : undefined,
            assignee: wf.currentAssignee
              ? { id: wf.currentAssignee.id, displayName: wf.currentAssignee.displayName }
              : null,
            dueDate: wf.dueDate ?? parentData.dueDate,
            slaStatus: (wf.slaStatus as WorkflowTableRow["slaStatus"]) ?? parentData.slaStatus,
          });
        }

        // Keep the cached domain object in sync with the same fresh response.
        // Click handlers read instanceRow from this cache and the drawer header
        // renders it, so without this the drawer would show stale
        // status/assignee/dueDate/progress after an expand.
        const cached = wf ? this.instanceCache.get(workflowId) : undefined;
        if (wf && cached) {
          this.instanceCache.set(workflowId, {
            ...cached,
            status: wf.status,
            statusCategory: wf.statusCategory ?? null,
            currentStepName: wf.currentStepName ?? null,
            currentAssignee: wf.currentAssignee
              ? { id: wf.currentAssignee.id, displayName: wf.currentAssignee.displayName }
              : null,
            dueDate: wf.dueDate ?? cached.dueDate,
            slaStatus: (wf.slaStatus as WorkflowInstanceRow["slaStatus"]) ?? cached.slaStatus,
            progress: wf.progress
              ? {
                  completed: wf.progress.completed,
                  total: wf.progress.total,
                  percentage: wf.progress.percentage,
                }
              : cached.progress,
          });
        }

        if (wf?.viewerWorkflowUserId != null && !this.viewerWorkflowUserIdFired) {
          this.viewerWorkflowUserIdFired = true;
          this.onViewerWorkflowUserIdResolved?.(String(wf.viewerWorkflowUserId));
        }

        params.success({ rowData: childRows, rowCount: childRows.length });
      })
      .catch(() => {
        toast.error("Failed to load tasks");
        params.fail();
      });
  }

  /** Jira-source workflows: lazy-fetch child items from Jira */
  private fetchJiraChildRows(
    params: AgGrid.IServerSideGetRowsParams,
    workflowId: string,
    parentIssueKey: string,
  ): void {
    this.client
      .query<GetJiraChildWorkItemsQuery, GetJiraChildWorkItemsQueryVariables>({
        query: GetJiraChildWorkItemsDocument,
        variables: { parentIssueKey },
        fetchPolicy: "network-only",
      })
      .then(({ data }) => {
        type JiraItem = GetJiraChildWorkItemsQuery["childWorkItems"]["items"][0];
        const items: JiraItem[] = data?.childWorkItems?.items ?? [];
        const childRows: WorkflowTableRow[] = items.map((item) => ({
          id: `${workflowId}-jira-${item.id}`,
          path: [workflowId, `${workflowId}-jira-${item.id}`],
          isParentRow: false,
          displayName: `${item.key} — ${item.summary}`,
          assignee: item.assignedTo?.displayName
            ? { id: item.id, displayName: item.assignedTo.displayName }
            : null,
          status: item.status,
        }));
        params.success({ rowData: childRows, rowCount: childRows.length });
      })
      .catch(() => {
        toast.error("Failed to load Jira sub-items");
        params.fail();
      });
  }
}
